import * as React from "react";
import "./css/ProjectsSummaryTable.css";
import { useDispatch } from "react-redux";
import { projectInfoActions } from "../../../store/features/ProjectInfoSlice";
import { dialogBoxRef } from "../../base/components/ContentRouter";
import ProjectContents from "../containers/ProjectContents";

interface Row {
  [key: string]: any;
}

interface IProps {
  data: Row[];
  width: number | string;
  selectedRowFunc?: any;
}

type GroupedData = {
  [region: string]: {
    projects: {
      [key: string]: {
        projects: Row[];
        donor_funding: number;
        adp_share: number;
        common_title: string;
        titles?: string[];
      };
    };
    donor_funding: number;
    adp_share: number;
    cumulative_spending: number;
    financial_progress_sum: number;
    physical_progress_sum: number;
    project_count: number;
    financial_progress_mean: number;
    physical_progress_mean: number;
    total_cost: number;
    total_additional_financing: number;
  };
};

const ProjectsSummaryTable = (props: IProps) => {
  const [selectedID, setSelectedID] = React.useState(null);
  const dispatch = useDispatch();
  // React.useEffect(() => {
  //   console.log("data", props.data);
  // }, [props.data]);
  // Utility function to find the longest common substring among an array of strings
  function longestCommonSubstring(
    strings: string[],
    exclude: string[] = [],
  ): string {
    if (!strings || strings.length === 0) return "";

    // Use the first string as reference; keep original case for the final return.
    let substr = strings[0];
    let lowerSubstr = substr.toLowerCase();

    for (let i = 1; i < strings.length; i++) {
      const lowerCurrent = strings[i].toLowerCase();

      // Trim from the end until it's found (case-insensitive) or empty.
      while (
        lowerSubstr.length > 0 &&
        lowerCurrent.indexOf(lowerSubstr) === -1
      ) {
        substr = substr.substring(0, substr.length - 1);
        lowerSubstr = lowerSubstr.substring(0, lowerSubstr.length - 1);
      }

      if (lowerSubstr.length === 0) return "";
    }

    // Post-process: remove excluded snippets and tidy punctuation/whitespace.
    let result = substr.trim();

    // Escape regex metacharacters for literal matches.
    const escapeRegExp = (s: string) =>
      s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    for (const bad of exclude) {
      if (!bad) continue;
      const rx = new RegExp(escapeRegExp(bad), "gi"); // case-insensitive, global
      result = result.replace(rx, "");
    }

    // Collapse multiple spaces and trim common separators/punctuation at ends.
    result = result
      .replace(/\s{2,}/g, " ")
      .replace(/^[\s\-—–·:.,/\\|…]+|[\s\-—–·:.,/\\|…]+$/g, "")
      .trim();

    return result;
  }

  const groupedData = props.data.reduce((acc: GroupedData, project: Row) => {
    const totalCost = parseFloat(
      (
        (project.donor_funding || 0) +
        (project.adp_share || 0) +
        (project.additional_financing || 0)
      ).toFixed(2),
    );
    const projectWithApprovedCost: Row = {
      ...project,
      total_cost: totalCost,
    };

    if (!acc[project.region]) {
      acc[project.region] = {
        projects: {},
        donor_funding: 0,
        adp_share: 0,
        cumulative_spending: 0,
        total_cost: 0,
        financial_progress_sum: 0,
        physical_progress_sum: 0,
        project_count: 0,
        financial_progress_mean: 0,
        physical_progress_mean: 0,
        total_additional_financing: 0,
      };
    }

    if (!acc[project.region].projects[project.project_id]) {
      acc[project.region].projects[project.project_id] = {
        projects: [],
        donor_funding: 0,
        adp_share: 0,
        common_title: "",
        titles: [],
      };
    }

    acc[project.region].projects[project.project_id].projects.push(
      projectWithApprovedCost,
    );
    acc[project.region].projects[project.project_id].donor_funding +=
      project.donor_funding || 0;
    acc[project.region].projects[project.project_id].adp_share +=
      project.adp_share || 0;
    acc[project.region].projects[project.project_id].titles!.push(
      project.title,
    );

    acc[project.region].donor_funding += project.donor_funding || 0;
    acc[project.region].adp_share += project.adp_share || 0;
    acc[project.region].cumulative_spending += project.Financial_Spending || 0;
    acc[project.region].total_additional_financing +=
      project.additional_financing || 0;
    acc[project.region].total_cost += totalCost;

    acc[project.region].financial_progress_sum +=
      project.financial_progress || 0;
    acc[project.region].physical_progress_sum += project.physical_progress || 0;
    acc[project.region].project_count += 1;

    return acc;
  }, {} as GroupedData);

  const excludeText = ["— OFWM"];
  // Calculate common titles and mean progress after grouping
  for (const region in groupedData) {
    const regionData = groupedData[region];
    if (region.toLocaleLowerCase() === "sindh") {
      console.log("Financial Spending:", regionData.cumulative_spending);
    }
    //
    regionData.financial_progress_mean = parseFloat(
      // (regionData.financial_progress_sum / regionData.project_count).toFixed(2)
      ((regionData.cumulative_spending / regionData.total_cost) * 100).toFixed(
        2,
      ),
    );
    regionData.physical_progress_mean = parseFloat(
      (regionData.physical_progress_sum / regionData.project_count).toFixed(2),
    );

    for (const projectId in regionData.projects) {
      const projectData = regionData.projects[projectId];
      projectData.common_title = longestCommonSubstring(
        projectData.titles!,
        excludeText,
      );
      delete projectData.titles;
    }
  }

  const totalSummary = Object.keys(groupedData).reduce(
    (totals: any, region: string) => {
      totals.donor_funding += groupedData[region].donor_funding;
      totals.adp_share += groupedData[region].adp_share;
      totals.cumulative_spending += groupedData[region].cumulative_spending;
      totals.total_cost += groupedData[region].total_cost;
      totals.financial_progress_sum +=
        groupedData[region].financial_progress_sum;
      totals.physical_progress_sum += groupedData[region].physical_progress_sum;
      totals.project_count += groupedData[region].project_count;
      totals.total_additional_financing +=
        groupedData[region].total_additional_financing;
      return totals;
    },
    {
      donor_funding: 0,
      adp_share: 0,
      cumulative_spending: 0,
      total_cost: 0,
      financial_progress_sum: 0,
      physical_progress_sum: 0,
      project_count: 0,
      financial_progress_mean: 0,
      physical_progress_mean: 0,
      total_additional_financing: 0,
    },
  );

  // Calculate mean financial and physical progress for total summary
  totalSummary.financial_progress_mean = parseFloat(
    // (totalSummary.financial_progress_sum / totalSummary.project_count).toFixed(2)
    (
      (totalSummary.cumulative_spending / totalSummary.total_cost) *
      100
    ).toFixed(2),
  );
  totalSummary.physical_progress_mean = parseFloat(
    (totalSummary.physical_progress_sum / totalSummary.project_count).toFixed(
      2,
    ),
  );

  const handleRowDblClick = (row: any) => {
    handleRowClick(row);
    // navigate(`/projectInfo?id=${selectedRow?.id}`);
    dialogBoxRef?.current?.setContent(
      <>
        <ProjectContents pid={row.id} showRightMenuBar={false} />
      </>,
    );
    dialogBoxRef?.current?.openDialog("Project Information");
  };
  const handleRowClick = (row: any) => {
    props.selectedRowFunc && props.selectedRowFunc(row);
    // console.log("row click", row)
    setSelectedID(row.id);
    dispatch(
      projectInfoActions.setProgress({
        pid: row.id,
        adpShare: row.adp_share,
        donorFund: row.donor_funding,
        physicalProgress: row.physical_progress,
        financialProgress: row.financial_progress,
      }),
    );
  };

  const getWidth = () => {
    return typeof props.width === "string" ? props.width : props.width * 3;
  };

  const projectData = [
    // { header: "Sr. No.", value: null, isNumber: false }, // Assuming Sr. No. does not have a corresponding key in the project object
    { header: "Project Name", value: "component_name", isNumber: false },
    // {header: "SRO", value: 'sector', isNumber: false},
    { header: "Dev Partner", value: "dev_partner", isNumber: false },
    { header: "Executing Agency", value: "exec_agency", isNumber: false },
    {
      header: "Donor Funds 4RF Allocated",
      value: "donor_funding",
      isNumber: true,
    },
    {
      header: "Additional Financing",
      value: "additional_financing",
      isNumber: true,
    },
    { header: "ADP / PSDP Share", value: "adp_share", isNumber: true },
    { header: "Total Cost", value: "total_cost", isNumber: true },
    {
      header: "Approving Authority",
      value: "approving_authority",
      isNumber: false,
    },
    { header: "Approval Date", value: "approval_date", isNumber: false },
    {
      header: "Financial Spending",
      value: "Financial_Spending",
      isNumber: true,
    },
    // { header: "Financial Progress (%)", value: 'financial_progress', isNumber: true },
    // { header: "Physical Progress (%)", value: 'physical_progress', isNumber: true } // Assuming this represents the physical progress percentage
  ];

  const no_of_columns = projectData.length + 3;
  let serial_no = 0;
  let sub_serial_no = 97;

  return (
    <React.Fragment>
      <table id="project-summary-table" style={{ width: getWidth() }}>
        <thead>
          <tr>
            <th>Sr. No.</th>
            {projectData.map((info, index) => (
              <th key={"info-head" + index}>{info.header}</th>
            ))}
            <th>Financial Progress (%)</th>
            <th>Physical Progress (%)</th>
          </tr>
        </thead>
        <tbody>
          {groupedData &&
            Object.keys(groupedData).map((region: string) => (
              <React.Fragment key={region}>
                <tr key={"tr-" + region}>
                  <td
                    key={"td-" + region}
                    colSpan={no_of_columns}
                    style={{
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      textAlign: "center",
                      backgroundColor: "#a9d08e",
                      color: "black",
                    }}
                  >
                    <span key={"span1-" + region} style={{ float: "left" }}>
                      Region:{" "}
                      {region == "Federal" ? "Cross-Provincial" : region}
                    </span>
                    <span key={"span2-" + region} style={{ float: "right" }}>
                      {" "}
                      US$ Million
                    </span>
                  </td>
                </tr>
                {Object.keys(groupedData[region].projects)?.map((key) => {
                  serial_no = serial_no + 1;
                  return (
                    <React.Fragment key={`${region}-${key}`}>
                      <tr key={`tr-${region}-${key}`}>
                        <td
                          key={`td-${region}-${key}`}
                          colSpan={no_of_columns}
                          style={{
                            fontSize: "16px",
                            backgroundColor: "#f2f2f2",
                            fontWeight: "bold",
                            color: "black",
                          }}
                        >
                          <span key={`span-title-${region}-${key}`}>
                            {serial_no +
                              ". " +
                              groupedData[region].projects[key]
                                .common_title}{" "}
                          </span>
                          <span
                            key={`span-title-donor-${region}-${key}`}
                            style={{ color: "#691818" }}
                          >
                            <b>
                              &nbsp;&nbsp;Total Donor Funding:{" "}
                              {groupedData[region].projects[
                                key
                              ].donor_funding.toFixed(2)}
                            </b>
                          </span>
                          <span
                            key={`span-title-approved-${region}-${key}`}
                            style={{ color: "#1d41ad" }}
                          >
                            <b>
                              &nbsp;&nbsp;Local Share:{" "}
                              {groupedData[region].projects[
                                key
                              ].adp_share.toFixed(2)}
                            </b>
                          </span>
                        </td>
                      </tr>
                      {groupedData[region].projects[key]["projects"].map(
                        (project: Row, projIndex: number) => {
                          return (
                            <tr
                              key={`tr-project-${region}-${key}-${projIndex}`}
                              onClick={() => handleRowClick(project)}
                              onDoubleClick={() => handleRowDblClick(project)}
                              style={{
                                backgroundColor:
                                  selectedID && selectedID === project.id
                                    ? "lightblue"
                                    : "white",
                                cursor: "pointer",
                              }}
                              className={"project-tr"}
                            >
                              <td
                                key={`td-project-${region}-${key}-${projIndex}-sr_no`}
                                className={"project-td"}
                              >
                                {String.fromCharCode(sub_serial_no + projIndex)}
                              </td>
                              {projectData.map((info, dataIndex: number) => (
                                <td
                                  key={`td-project-${region}-${key}-${projIndex}-${dataIndex}`}
                                  className={"project-td"}
                                  style={{
                                    textAlign: info.isNumber
                                      ? "center"
                                      : "left",
                                    ...(info.value === "approval_date"
                                      ? { whiteSpace: "nowrap" }
                                      : {}),
                                  }}
                                >
                                  {project[info.value]}
                                </td>
                              ))}

                              <td
                                style={{ verticalAlign: "middle", padding: 0 }}
                              >
                                <div
                                  style={{
                                    height: "30px",
                                    width: "90%",
                                    padding: 0,
                                    position: "relative",
                                    border: "1px solid black",
                                    backgroundColor: "lightgray",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      height: "100%",
                                      width: `${project.financial_progress}%`,
                                      backgroundColor: "#4CAF50",
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      padding: 0,
                                    }}
                                  ></div>
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "50%",
                                      left: "50%",
                                      transform: "translate(-50%, -50%)",
                                      color: "black",
                                    }}
                                  >
                                    {project.financial_progress}%
                                  </div>
                                </div>
                              </td>
                              <td
                                style={{ verticalAlign: "middle", padding: 0 }}
                              >
                                <div
                                  style={{
                                    height: "30px",
                                    width: "90%",
                                    padding: 0,
                                    position: "relative",
                                    border: "1px solid black",
                                    backgroundColor: "lightgray",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      height: "100%",
                                      width: `${project.physical_progress}%`,
                                      backgroundColor: "#4CAF50",
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      padding: 0,
                                    }}
                                  ></div>
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "50%",
                                      left: "50%",
                                      transform: "translate(-50%, -50%)",
                                      color: "black",
                                    }}
                                  >
                                    {project.physical_progress}%
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        },
                      )}
                    </React.Fragment>
                  );
                })}
                <tr
                  key={`${region}-summary`}
                  style={{ color: "black", backgroundColor: "#ffc000" }}
                >
                  <td
                    colSpan={4}
                    style={{
                      fontSize: "18px",
                      textAlign: "right",
                      color: "#b32525",
                    }}
                  >
                    {region} Portfolio
                  </td>
                  <td
                    style={{
                      fontSize: "16px",
                      textAlign: "center",
                      color: "#b32525",
                    }}
                  >
                    {groupedData[region].donor_funding.toFixed(2)}
                  </td>
                  <td
                    style={{
                      fontSize: "16px",
                      textAlign: "center",
                      color: "#b32525",
                    }}
                  >
                    {groupedData[region].total_additional_financing.toFixed(2)}
                  </td>
                  <td
                    style={{
                      textAlign: "center",
                      fontSize: "16px",
                      color: "#b32525",
                    }}
                  >
                    {groupedData[region].adp_share.toFixed(2)}
                  </td>
                  <td
                    style={{
                      textAlign: "center",
                      fontSize: "16px",
                      color: "#b32525",
                    }}
                  >
                    {groupedData[region].total_cost.toFixed(2)}
                  </td>
                  <td colSpan={2}></td>
                  <td
                    style={{
                      fontSize: "16px",
                      textAlign: "center",
                      color: "#b32525",
                    }}
                  >
                    {groupedData[region].cumulative_spending.toFixed(2)}
                  </td>

                  <td style={{ verticalAlign: "middle", padding: 0 }}>
                    <div
                      style={{
                        height: "30px",
                        width: "90%",
                        padding: 0,
                        position: "relative",
                        border: "1px solid black",
                        backgroundColor: "lightgray",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${groupedData[region].financial_progress_mean.toFixed(2)}%`,
                          backgroundColor: "#4CAF50",
                          position: "absolute",
                          top: 0,
                          left: 0,
                          padding: 0,
                        }}
                      ></div>
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          color: "#3f0808",
                        }}
                      >
                        {groupedData[region].financial_progress_mean.toFixed(2)}
                        %
                      </div>
                    </div>
                  </td>

                  <td style={{ verticalAlign: "middle", padding: 0 }}>
                    {/*<div*/}
                    {/*    style={{*/}
                    {/*        height: "30px",*/}
                    {/*        width: "90%",*/}
                    {/*        padding: 0,*/}
                    {/*        position: "relative",*/}
                    {/*        border: "1px solid black",*/}
                    {/*        backgroundColor: "lightgray",*/}
                    {/*        overflow: "hidden",*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    <div*/}
                    {/*        style={{*/}
                    {/*            height: "100%",*/}
                    {/*            width: `${groupedData[region].physical_progress_mean.toFixed(2)}%`,*/}
                    {/*            backgroundColor: "#4CAF50",*/}
                    {/*            position: "absolute",*/}
                    {/*            top: 0,*/}
                    {/*            left: 0,*/}
                    {/*            padding: 0,*/}
                    {/*        }}*/}
                    {/*    ></div>*/}
                    {/*    <div*/}
                    {/*        style={{*/}
                    {/*            position: "absolute",*/}
                    {/*            top: "50%",*/}
                    {/*            left: "50%",*/}
                    {/*            transform: "translate(-50%, -50%)",*/}
                    {/*            color: "#3f0808",*/}
                    {/*        }}*/}
                    {/*    >*/}
                    {/*        {groupedData[region].physical_progress_mean.toFixed(2)}%*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                  </td>
                </tr>
              </React.Fragment>
            ))}
          <tr
            key={"total-summary"}
            style={{ color: "white", backgroundColor: "#333333" }}
          >
            <td
              colSpan={4}
              style={{
                fontSize: "18px",
                textAlign: "right",
                color: "white",
              }}
            >
              Total Portfolio
            </td>
            <td
              style={{
                fontSize: "16px",
                textAlign: "center",
                color: "white",
              }}
            >
              {totalSummary.donor_funding.toFixed(2)}
            </td>
            <td
              style={{
                textAlign: "center",
                fontSize: "16px",
                color: "white",
              }}
            >
              {totalSummary.total_additional_financing.toFixed(2)}
            </td>
            <td
              style={{
                textAlign: "center",
                fontSize: "16px",
                color: "white",
              }}
            >
              {totalSummary.adp_share.toFixed(2)}
            </td>
            <td
              style={{
                textAlign: "center",
                fontSize: "16px",
                color: "white",
              }}
            >
              {totalSummary.total_cost.toFixed(2)}
            </td>
            <td colSpan={2}></td>
            <td
              style={{
                fontSize: "16px",
                textAlign: "center",
                color: "white",
              }}
            >
              {totalSummary.cumulative_spending.toFixed(2)}
            </td>
            <td style={{ verticalAlign: "middle", padding: 0 }}>
              <div
                style={{
                  height: "30px",
                  width: "90%",
                  padding: 0,
                  position: "relative",
                  border: "1px solid black",
                  backgroundColor: "lightgray",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${totalSummary.financial_progress_mean.toFixed(2)}%`,
                    backgroundColor: "#4CAF50",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    padding: 0,
                  }}
                ></div>
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: "black",
                  }}
                >
                  {totalSummary.financial_progress_mean.toFixed(2)}%
                </div>
              </div>
            </td>

            <td style={{ verticalAlign: "middle", padding: 0 }}>
              {/*<div*/}
              {/*    style={{*/}
              {/*        height: "30px",*/}
              {/*        width: "90%",*/}
              {/*        padding: 0,*/}
              {/*        position: "relative",*/}
              {/*        border: "1px solid black",*/}
              {/*        backgroundColor: "lightgray",*/}
              {/*        overflow: "hidden",*/}
              {/*    }}*/}
              {/*>*/}
              {/*    <div*/}
              {/*        style={{*/}
              {/*            height: "100%",*/}
              {/*            width: `${totalSummary.physical_progress_mean.toFixed(2)}%`,*/}
              {/*            backgroundColor: "#4CAF50",*/}
              {/*            position: "absolute",*/}
              {/*            top: 0,*/}
              {/*            left: 0,*/}
              {/*            padding: 0,*/}
              {/*        }}*/}
              {/*    ></div>*/}
              {/*    <div*/}
              {/*        style={{*/}
              {/*            position: "absolute",*/}
              {/*            top: "50%",*/}
              {/*            left: "50%",*/}
              {/*            transform: "translate(-50%, -50%)",*/}
              {/*            color: "black",*/}
              {/*        }}*/}
              {/*    >*/}
              {/*        {totalSummary.physical_progress_mean.toFixed(2)}%*/}
              {/*    </div>*/}
              {/*</div>*/}
            </td>
          </tr>
        </tbody>
      </table>
    </React.Fragment>
  );
};

export default ProjectsSummaryTable;
